import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getStockholmLocalDateNow } from "components/designV2/datePicker/datePicker.utils";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { MonthPeriodNavigator } from "components/designV2/periodNavigator/monthPeriodNavigator.component";
import { Text } from "components/designV2/text/text";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import type { CompanyBankAccountType } from "model/Company";
import { getSettingValue } from "model/settings.logic";
import { Api } from "services/api/api.service";
import { FileViewer } from "web-apps/company/components/fileViewer/fileViewer.component";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  HeaderRightAligned,
  IndividualSalaryFilters,
  PeriodNavigatorContainer,
  SalaryPeriodCard,
  SalaryPageContainer,
} from "./salary.styled";
import { AGIList } from "./agiList.component";
import { SalaryPayoutPanel } from "./salaryPayoutPanel.component";
import { useSalaryPayoutPanel } from "./useSalaryPayoutPanel";

export const AGIOverview = (): JSX.Element => {
  const [payoutMonth, setPayoutMonth] = useState(() =>
    getStockholmLocalDateNow().withDayOfMonth(1),
  );
  // Preserve the API period while displaying the following payout month.
  const workMonth = useMemo(() => payoutMonth.minusMonths(1), [payoutMonth]);
  const [companyState] = useCompanyStore();
  const company = companyState.company;
  const { t } = useTranslation();
  const salaryPayoutPanel = useSalaryPayoutPanel({
    companyId: company?.id,
    workMonth,
  });
  const settings = companyState.companySettings;
  const bankAgreementId =
    getSettingValue(settings, "CompanyBankAgreementId") ?? "";
  const bankAccountValue =
    getSettingValue(settings, "CompanyBankAccountValue") ?? "";
  const bankAccountTypeValue = getSettingValue(
    settings,
    "CompanyBankAccountType",
  );
  const bankAccountType: CompanyBankAccountType =
    bankAccountTypeValue === "InternationalBankAccount"
      ? "InternationalBankAccount"
      : "BankAccount";

  const fetchAGIFile = useCallback(() => {
    if (!company) {
      return Promise.reject(new Error("Missing company"));
    }

    return Api().company.salary.getAGI(
      company.id,
      workMonth.year(),
      workMonth.monthValue(),
    );
  }, [company, workMonth]);

  return (
    <SalaryPageContainer>
      <HeaderRightAligned>
        <TooltipHover text={t("Salary.AGIInfo")} arrow="up">
          <Text typography="s1" color={ColorV2.Blue1}>
            {t("Salary.WhatIs", { name: t("Salary.AGI").toLowerCase() })}
          </Text>
        </TooltipHover>
      </HeaderRightAligned>
      <SalaryPeriodCard>
        <IndividualSalaryFilters>
          <PeriodNavigatorContainer>
            <MonthPeriodNavigator
              currentDate={payoutMonth}
              onChange={setPayoutMonth}
              showTodayButton
            />
          </PeriodNavigatorContainer>
        </IndividualSalaryFilters>
      </SalaryPeriodCard>

      {company && (
        <FileViewer
          fetchEndpoint={fetchAGIFile}
          filename={`AGI_${payoutMonth.year()}_${payoutMonth.monthValue()}.xml`}
          type="application/xml"
          currentDate={workMonth}
          extraActions={
            <PrimaryButton
              label={t("Salary.PayOutSalaries")}
              size="small"
              onClick={salaryPayoutPanel.openPanel}
            />
          }
          ListComponent={AGIList}
        />
      )}
      <SalaryPayoutPanel
        bankAgreementId={bankAgreementId}
        bankAccountType={bankAccountType}
        bankAccountValue={bankAccountValue}
        canCreatePayoutFile={salaryPayoutPanel.canCreatePayoutFile}
        currentDate={payoutMonth}
        excludedWorkerCount={salaryPayoutPanel.excludedWorkerCount}
        isCreatingPayoutFile={salaryPayoutPanel.isCreatingPayoutFile}
        isOpen={salaryPayoutPanel.isOpen}
        payoutDate={salaryPayoutPanel.payoutDate}
        payoutWorkerCount={salaryPayoutPanel.payoutWorkerCount}
        previewState={salaryPayoutPanel.previewState}
        totalNetPayoutAmount={salaryPayoutPanel.totalNetPayoutAmount}
        onClose={salaryPayoutPanel.closePanel}
        onCreate={salaryPayoutPanel.createPayoutFile}
        onPayoutDateChange={salaryPayoutPanel.setPayoutDate}
      />
    </SalaryPageContainer>
  );
};
